import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useFarm } from "@/context/FarmContext";
import { PageHeader, NoFarm } from "@/components/Page";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

/**
 * Generic CRUD module. Configure via props to build a data-managed page.
 * fields: [{ key, label, type: 'text'|'number'|'date'|'select', options?, numeric?, step? }]
 */
export function CrudModule({
  title,
  subtitle,
  resource,
  fields,
  defaults,
  testid,
  beforeTable,
  rowExtras,
}) {
  const { farmId, loading } = useFarm();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaults);

  const base = `/farms/${farmId}/${resource}`;
  const key = [resource, farmId];

  const { data: items = [] } = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get(base)).data,
    enabled: !!farmId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: key });
    qc.invalidateQueries({ queryKey: ["dashboard", farmId] });
    qc.invalidateQueries({ queryKey: ["grass", farmId] });
    qc.invalidateQueries({ queryKey: ["recs", farmId] });
  };

  const save = useMutation({
    mutationFn: async (payload) =>
      editing ? api.put(`${base}/${editing.id}`, payload) : api.post(base, payload),
    onSuccess: () => { invalidate(); setOpen(false); toast.success(editing ? "Updated" : "Added"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: async (id) => api.delete(`${base}/${id}`),
    onSuccess: () => { invalidate(); toast.success("Deleted"); },
  });

  const openNew = () => { setEditing(null); setForm(defaults); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...defaults, ...it }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const payload = {};
    fields.forEach((f) => {
      if (f.render && !f.type) return;   // computed display-only column
      let v = form[f.key];
      if (f.numeric) v = v === "" || v == null ? 0 : Number(v);
      payload[f.key] = v ?? "";
    });
    save.mutate(payload);
  };

  if (!loading && !farmId) return <NoFarm />;

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle} action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid={`${testid}-add-button`} onClick={openNew}>
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit" : "Add"} {title.replace(/s$/, "")}</DialogTitle>
              <DialogDescription>Enter the details below in kg DM, hectares and euro.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {fields.filter((f) => !(f.render && !f.type)).map((f) => (
                <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                  <Label>{f.label}</Label>
                  {f.type === "select" ? (
                    <Select value={String(form[f.key] ?? "")} onValueChange={(v) => set(f.key, v)}>
                      <SelectTrigger className="mt-1" data-testid={`${testid}-${f.key}-input`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      type={f.type || "text"}
                      step={f.step || "any"}
                      data-testid={`${testid}-${f.key}-input`}
                      value={form[f.key] ?? ""}
                      onChange={(e) => set(f.key, e.target.value)}
                      required={f.key === "name"}
                      className="mt-1"
                    />
                  )}
                </div>
              ))}
              <DialogFooter className="sm:col-span-2">
                <Button type="submit" className="rounded-full" data-testid={`${testid}-save-button`} disabled={save.isPending}>
                  {editing ? "Save" : "Add"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      } />

      {beforeTable && beforeTable(items, invalidate)}

      <Card className="overflow-hidden">
        <Table data-testid={`${testid}-table`}>
          <TableHeader>
            <TableRow>
              {fields.map((f) => (
                <TableHead key={f.key} className={f.numeric ? "text-right" : ""}>{f.label}</TableHead>
              ))}
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={fields.length + 1} className="text-center text-muted-foreground py-10">
                No entries yet. Click Add to create one.
              </TableCell></TableRow>
            )}
            {items.map((it, idx) => (
              <TableRow key={it.id} className={idx % 2 ? "bg-secondary/40" : ""} data-testid={`${testid}-row-${it.id}`}>
                {fields.map((f) => (
                  <TableCell key={f.key} className={f.numeric ? "text-right tabular-nums" : "font-medium"}>
                    {f.render ? f.render(it) : (it[f.key] ?? "—")}
                  </TableCell>
                ))}
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" data-testid={`${testid}-edit-${it.id}`} onClick={() => openEdit(it)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-destructive" data-testid={`${testid}-delete-${it.id}`}
                    onClick={() => window.confirm("Delete this entry?") && del.mutate(it.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      {rowExtras && rowExtras(items)}
    </div>
  );
}
